import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'

import clsx from 'clsx'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import Image from 'components/Common/Image'
import TextButton from 'LuxKit/Button/TextButton'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Sheet from 'components/utils/Sheet'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import * as Analytics from 'analytics/analytics'
import { promotionClickEvent, promotionViewEvent } from 'analytics/snowplow/events'
import SolidTagIcon from 'LuxKit/Icons/solid/SolidTagIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import { useTourDeal } from 'hooks/TourV2/useTourDeal'

const MainContainer = styled.div`
  overflow: hidden;

  &.is-carousel {
    overflow: visible;
    width: ${rem(300)};

    ${mediaQueryUp.tablet} {
      width: ${rem(560)};
    }
  }
`

const ViewAllButton = styled(TextButton)`
  display: none;

  ${mediaQueryUp.tablet} {
    display: block;
    position: relative;
    opacity: 0;
    transition: opacity 0.2s;
  }
`
const ViewAllButtonContainer = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    position: absolute;
    z-index: 1;
    right: ${rem(20)};
    bottom: ${rem(20)};
  }
`

const DealsContainer = styled.div`
  display: flex;
  position: relative;
  height: ${rem(125)};

  ${mediaQueryUp.tablet} {
    height: ${rem(170)};
  }

  &:hover ${ViewAllButton} {
    opacity: 1;
  }
`

const ColourContainer = styled(Sheet)`
  padding: ${rem(16)};
  width: 60%;
  height: 100%;

  &.is-carousel {
    width: 100%;

    ${mediaQueryUp.tablet} {
      width: 55%;
    }
  }

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)};
    width: 55%;
    height: 100%;
  }
`

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  overflow: hidden;
  width: 40%;
  height: 100%;

  &.is-carousel {
    width: ${rem(260)};
  }

  ${mediaQueryUp.tablet} {
    width: 45%;
  }
`

const TileSubheading = styled(BodyText).attrs({
  variant: 'large',
  weight: 'bold',
})``

interface Props {
  slug: string
}

function TourDealTile(props: Props) {
  const intl = useIntl()
  const { slug } = props

  const [deal, fetching] = useTourDeal(slug)

  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: 0.5, // At least 50% of the element must be visible
  })

  useEffect(() => {
    if (inView) {
      Analytics.trackEvent(promotionViewEvent({
        promotionContext: {
          promotionName: slug,
          promotionType: 'banner',
        },
      }))
    }
  }, [inView, slug])

  const onDealClick = useCallback(() => {
    Analytics.trackEvent(promotionClickEvent({
      promotionContext: {
        promotionName: slug,
        promotionType: 'banner',
      },
    }))
  }, [slug])

  if (fetching || !deal) {
    return null
  }
  return <ClickableLink to={`/tours/deals/${slug}`} onClick={onDealClick} preventSwipeClick>
    <MainContainer className={clsx({ 'is-carousel': true })} ref={inViewRef}>
      <Group
        direction="vertical"
        gap="16 0"
        tabletGap="12 0"
      >
        <DealsContainer>
          <ColourContainer
            paletteType="inverse"
            className={clsx({ 'is-carousel': true })}
            style={{ backgroundColor: deal.highlightColour }}
          >
            <Group direction="vertical" verticalAlign="center" fullHeight>
              <LabelGroup>
                {deal.dealType === 'LUX_PLUS' && <LuxPlusLogo colour="inherit" height={12} style={{ }} />}
                {deal.dealType === 'LIMITED_TIME_SALE' && <UrgencyLabel
                  variant="critical2"
                  icon={<SolidTagIcon />}
                >
                  {intl.formatMessage(
                    {
                      id: 'wa31P8C6UN4SAi0fZx2u',
                      defaultMessage: 'Limited Time Special',
                      description: 'Urgency label shown on tour deal tiles for limited-time sale offers',
                    },
                  )}
                </UrgencyLabel>}
              </LabelGroup>
              <CSSBreakpoint max="mobile">
                <Heading as="h6" variant="heading6">
                  {deal.heading}
                </Heading>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <Heading as="h4" variant="heading4">
                  {deal.heading}
                </Heading>
              </CSSBreakpoint>
            </Group>
          </ColourContainer>
          <ImageContainer className={clsx({ 'is-carousel': true })}>
            <Image
              fit="center"
              id={deal.heroImageId}
              src={deal.heroImageId}
              role="presentation"
              alt={deal.heading}
            />
            <ViewAllButtonContainer>
              <ViewAllButton kind="primary" variant="ghost" size="small">
                {intl.formatMessage(
                  {
                    id: 'hYpF6fwKQAc0asfWYkaD',
                    defaultMessage: 'View all offers',
                    description: 'Button label on tour deal tile to view all available offers',
                  },
                )}
              </ViewAllButton>
            </ViewAllButtonContainer>
          </ImageContainer>
        </DealsContainer>
        <Group direction="vertical" gap={4}>
          {deal.subHeading && <TileSubheading variant="large" weight="bold">{deal.subHeading}</TileSubheading>}
        </Group>
      </Group>
    </MainContainer>
  </ClickableLink>
}

export default TourDealTile
